import type { Team } from '@compass/client/types/team';
import { Edit2, MoreVertical, Trash2, Users as UsersIcon } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { EditTeamDialog } from '@/components/settings/EditTeamDialog';
import { ManageTeamMembersDialog } from '@/components/settings/ManageTeamMembersDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserCell } from '@/components/UserCell';
import { useProgramTeams, useTeamMembersMap, useUnlinkTeamFromProgram } from '@/hooks/useTeams';
import { useStore } from '@/store';
import { getApiErrorMessage } from '@/utils/apiError';

export const ProgramTeams: React.FC = () => {
  const { selectedOrganizationId, selectedProgramId } = useStore();

  const [manageMembersTeam, setManageMembersTeam] = useState<Team | null>(null);
  const [editTeam, setEditTeam] = useState<Team | null>(null);

  const { teams, isLoading } = useProgramTeams();
  const { usersMap } = useTeamMembersMap(teams);
  const unlinkMutation = useUnlinkTeamFromProgram();

  const handleUnlink = async (team: Team) => {
    if (!team.id || !selectedOrganizationId || !selectedProgramId) return;

    try {
      await unlinkMutation.mutateAsync({
        organizationId: selectedOrganizationId,
        programId: selectedProgramId,
        teamId: team.id,
      });
      toast.success(`Team "${team.name || team.id}" removed from program`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to unlink team'));
    }
  };

  if (!selectedOrganizationId || !selectedProgramId) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="py-8 text-center text-muted-foreground">
          Please select an organization and program to view teams.
        </div>
      </div>
    );
  }

  const isEmpty = !isLoading && teams.length === 0;

  return (
    <div className="rounded-lg border bg-card p-6">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">Teams</h2>
          <p className="text-sm text-muted-foreground">Teams linked to this program.</p>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : isEmpty ? (
        <div className="py-8 text-center text-muted-foreground">
          <UsersIcon className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <p className="mt-4">No teams linked to this program</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <div className="max-h-[340px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 border-b bg-card">
                <TableRow>
                  <TableHead>Team Name</TableHead>
                  <TableHead>Team Lead</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {teams.map(team => {
                  const manager = team.manager_id ? usersMap.get(team.manager_id) : null;
                  const memberCount = team.member_ids?.length || 0;

                  return (
                    <TableRow key={team.id}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{team.name || 'Unnamed Team'}</span>
                          <p className="text-sm text-muted-foreground">
                            {memberCount} {memberCount === 1 ? 'member' : 'members'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {team.manager_id ? (
                          <UserCell user={manager ?? undefined} fallbackId={team.manager_id} />
                        ) : (
                          <span className="text-sm text-muted-foreground">{'\u2014'}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setManageMembersTeam(team)}>
                              <UsersIcon className="mr-2 h-4 w-4" />
                              Manage Members
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditTeam(team)}>
                              <Edit2 className="mr-2 h-4 w-4" />
                              Edit Team
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => handleUnlink(team)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove from Program
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Dialogs */}
      {selectedOrganizationId && selectedProgramId && (
        <>
          <EditTeamDialog
            organizationId={selectedOrganizationId}
            programId={selectedProgramId}
            team={editTeam}
            open={!!editTeam}
            onOpenChange={open => !open && setEditTeam(null)}
          />
          <ManageTeamMembersDialog
            organizationId={selectedOrganizationId}
            team={manageMembersTeam}
            open={!!manageMembersTeam}
            onOpenChange={open => !open && setManageMembersTeam(null)}
          />
        </>
      )}
    </div>
  );
};
